import { useState, useEffect, useCallback, useRef } from 'react';
import type { Page, HomeViewData, UsePageReturn, PageType } from '@/types/page';
import { pageService } from '@/services/page.service';
import { postMessageService } from '@/services/postMessage.service';
import { PageMapper } from '@/lib/pageMapper';
import { useTenant } from './useTenant';

const STORAGE_KEYS = {
  PAGE_CACHE: 'inmocms_page_cache',
  LAST_FETCH: 'inmocms_page_last_fetch',
} as const;

// Cache duration: disabled in dev (0), 3 minutes in production
const CACHE_DURATION = import.meta.env.DEV ? 0 : 3 * 60 * 1000;

interface UsePage {
  /**
   * Get page by slug
   */
  (slug: string): UsePageReturn;
}

interface UseHomePage {
  /**
   * Get home page specifically
   */
  (): UsePageReturn;
}

/**
 * Detect if running in iframe
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to same-origin policy, we're likely in an iframe
    return true;
  }
}

/**
 * Hook for fetching any page by slug
 */
export function usePage(slug: string): UsePageReturn {
  const [page, setPage] = useState<Page | null>(null);
  const [viewData, setViewData] = useState<HomeViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { config: tenantConfig, loading: tenantLoading } = useTenant();
  const isLoading = useRef(false);
  const currentPageRef = useRef<Page | null>(null);
  const inIframe = isInIframe();

  // Update ref when page changes
  useEffect(() => {
    currentPageRef.current = page;
  }, [page]);

  /**
   * Generate cache key for the specific page - stable function
   */
  const getCacheKey = useCallback((pageSlug: string) => {
    return `${STORAGE_KEYS.PAGE_CACHE}_${pageSlug}`;
  }, []);

  /**
   * Load cached page data - stable function (only for non-iframe mode)
   */
  const loadCachedPage = useCallback((pageSlug: string): Page | null => {
    if (inIframe) return null; // No cache for iframe mode
    
    try {
      const cacheKey = getCacheKey(pageSlug);
      const cachedData = localStorage.getItem(cacheKey);
      const lastFetch = localStorage.getItem(`${STORAGE_KEYS.LAST_FETCH}_${pageSlug}`);
      
      if (cachedData && lastFetch) {
        const cacheAge = Date.now() - parseInt(lastFetch);
        if (cacheAge < CACHE_DURATION) {
          return JSON.parse(cachedData);
        }
      }
    } catch (error) {
      console.warn('Failed to load cached page data:', error);
    }
    return null;
  }, [getCacheKey, inIframe]);

  /**
   * Save page data to cache - stable function (only for non-iframe mode)
   */
  const savePageToCache = useCallback((pageSlug: string, pageData: Page): void => {
    if (inIframe) return; // No cache for iframe mode
    
    try {
      const cacheKey = getCacheKey(pageSlug);
      localStorage.setItem(cacheKey, JSON.stringify(pageData));
      localStorage.setItem(`${STORAGE_KEYS.LAST_FETCH}_${pageSlug}`, Date.now().toString());
    } catch (error) {
      console.warn('Failed to cache page data:', error);
    }
  }, [getCacheKey, inIframe]);

  /**
   * Load page data via postMessage (iframe mode)
   */
  const loadPageDataViaPostMessage = useCallback(async (pageSlug: string): Promise<void> => {
    console.log('usePage: Loading page via postMessage:', pageSlug);
    setLoading(true);
    setError(null);

    try {
      // First try to get specific page by slug
      let pageData: Page;
      
      if (pageSlug === 'home' || pageSlug === 'inicio') {
        pageData = await postMessageService.getHomePage();
      } else {
        pageData = await postMessageService.getPageBySlug(pageSlug);
      }
      
      console.log('usePage: Page data received from postMessage:', pageData);
      setPage(pageData);
      setError(null);
    } catch (err) {
      console.error('usePage: PostMessage fetch failed:', err);
      
      if (tenantConfig) {
        const fallbackViewData = PageMapper.getDefaultHomeViewData(tenantConfig);
        setViewData(fallbackViewData);
        setError('Usando configuración por defecto. Comunicación con CMS falló.');
      } else {
        setError('Error al cargar contenido de la página via PostMessage.');
      }
    } finally {
      setLoading(false);
    }
  }, [postMessageService, tenantConfig]);

  /**
   * Load page data from backend or cache - stable function (non-iframe mode)
   */
  const loadPageDataViaAPI = useCallback(async (pageSlug: string, forceRefresh = false): Promise<void> => {
    
    if (isLoading.current && !forceRefresh) {
      return;
    }

    isLoading.current = true;
    setLoading(true);
    setError(null);

    try {
      // Try cache first if not forcing refresh
      if (!forceRefresh) {
        const cachedPage = loadCachedPage(pageSlug);
        if (cachedPage) {
          setPage(cachedPage);
          
          // Continue to fetch fresh data in background if user has access
          if (!pageService.hasPageAccess()) {
            setLoading(false);
            isLoading.current = false;
            return;
          }
        }
      }

      // Fetch from backend
      try {
        const pageData = await pageService.getPageBySlug(pageSlug, true);
        
        
        setPage(pageData);
        savePageToCache(pageSlug, pageData);
        setError(null);
      } catch (apiError) {
        console.error('API fetch failed for page:', apiError);
        
        // If we don't have cached data, create fallback
        if (!currentPageRef.current && tenantConfig) {
          const fallbackViewData = PageMapper.getDefaultHomeViewData(tenantConfig);
          setViewData(fallbackViewData);
          setError('Usando configuración por defecto. Contenido limitado.');
        } else if (currentPageRef.current) {
          setError('Usando contenido en caché. Cambios recientes no visibles.');
        } else {
          setError('Error al cargar contenido de la página.');
        }
      }

    } catch (error) {
      console.error('Error loading page data:', error);
      
      // Use fallback data if available
      if (tenantConfig) {
        const fallbackViewData = PageMapper.getDefaultHomeViewData(tenantConfig);
        setViewData(fallbackViewData);
        setError('Usando configuración por defecto debido a error.');
      } else {
        setError('Error al cargar contenido de la página.');
      }
    } finally {
      setLoading(false);
      isLoading.current = false;
    }
  }, [loadCachedPage, savePageToCache, tenantConfig]);

  /**
   * Load page data - routes to appropriate method based on context
   */
  const loadPageData = useCallback(async (pageSlug: string, forceRefresh = false): Promise<void> => {
    
    console.log('usePage: Loading page data', { pageSlug, inIframe, forceRefresh });
    
    if (inIframe) {
      await loadPageDataViaPostMessage(pageSlug);
    } else {
      await loadPageDataViaAPI(pageSlug, forceRefresh);
    }
  }, [inIframe, loadPageDataViaPostMessage, loadPageDataViaAPI]);

  /**
   * Refetch page data - stable function
   */
  const refetch = useCallback(async (): Promise<void> => {
    await loadPageData(slug, true);
  }, [loadPageData, slug]);

  // Setup postMessage listener for iframe mode
  useEffect(() => {
    if (inIframe) {
      console.log('usePage: Setting up postMessage listener for iframe mode');
      
      const handlePreviewUpdate = (data: any) => {
        console.log('usePage: Received preview update:', data);
        if (data.page && (data.page.slug === slug || (slug === 'home' && data.page.type === 'HOME'))) {
          setPage(data.page);
          setError(null);
        }
      };

      postMessageService.onPreviewUpdate(handlePreviewUpdate);
      
      return () => {
        postMessageService.cleanup();
      };
    }
  }, [inIframe, slug]);

  // Load page when slug changes or tenant config is available
  useEffect(() => {
    if (!tenantLoading && tenantConfig && slug && !isLoading.current) {
      loadPageData(slug);
    }
  }, [slug, tenantConfig, tenantLoading, loadPageData]);

  // Update view data when page or tenant config changes
  useEffect(() => {
    if (page && tenantConfig && !loading) {
      const mappedViewData = PageMapper.toViewData(page, tenantConfig);
      setViewData(mappedViewData);
    }
  }, [page, tenantConfig, loading]);

  return {
    page,
    viewData,
    loading: loading || tenantLoading,
    error,
    refetch,
  };
}

/**
 * Hook specifically for home page - optimized version
 */
export function useHomePage(): UsePageReturn {
  const [page, setPage] = useState<Page | null>(null);
  const [viewData, setViewData] = useState<HomeViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { config: tenantConfig, loading: tenantLoading } = useTenant();
  const isLoading = useRef(false);
  const currentPageRef = useRef<Page | null>(null);
  const currentTenantRef = useRef<typeof tenantConfig>(null);
  const inIframe = isInIframe();

  // Update refs when values change
  useEffect(() => {
    currentPageRef.current = page;
  }, [page]);

  useEffect(() => {
    currentTenantRef.current = tenantConfig;
  }, [tenantConfig]);

  /**
   * Load home page data via postMessage (iframe mode)
   */
  const loadHomePageDataViaPostMessage = useCallback(async (): Promise<void> => {
    console.log('useHomePage: Loading via postMessage');
    setLoading(true);
    setError(null);

    try {
      const homePageData = await postMessageService.getHomePage();
      console.log('useHomePage: Data received from postMessage:', homePageData);
      setPage(homePageData);
      setError(null);
    } catch (err) {
      console.error('useHomePage: PostMessage fetch failed:', err);
      
      const currentTenant = currentTenantRef.current;
      if (currentTenant) {
        const fallbackViewData = PageMapper.getDefaultHomeViewData(currentTenant);
        setViewData(fallbackViewData);
        setError('Usando configuración por defecto. Comunicación con CMS falló.');
      } else {
        setError('Error al cargar página de inicio via PostMessage.');
      }
    } finally {
      setLoading(false);
    }
  }, [postMessageService]);

  /**
   * Load home page data via API - stable function (non-iframe mode)
   */
  const loadHomePageDataViaAPI = useCallback(async (forceRefresh = false): Promise<void> => {
    if (isLoading.current && !forceRefresh) {
      return;
    }

    isLoading.current = true;
    setLoading(true);
    setError(null);

    try {
      // Try cache first
      const cacheKey = `${STORAGE_KEYS.PAGE_CACHE}_home`;
      if (!forceRefresh) {
        const cachedData = localStorage.getItem(cacheKey);
        const lastFetch = localStorage.getItem(`${STORAGE_KEYS.LAST_FETCH}_home`);
        
        if (cachedData && lastFetch) {
          const cacheAge = Date.now() - parseInt(lastFetch);
          if (cacheAge < CACHE_DURATION) {
            const cachedPage = JSON.parse(cachedData);
            setPage(cachedPage);
            
            // Continue to fetch fresh data in background if user has access
            if (!pageService.hasPageAccess()) {
              setLoading(false);
              isLoading.current = false;
              return;
            }
          }
        }
      }

      // Fetch home page from backend
      try {
        const homePageData = await pageService.getHomePage();
        
        setPage(homePageData);
        
        // Cache the data
        localStorage.setItem(cacheKey, JSON.stringify(homePageData));
        localStorage.setItem(`${STORAGE_KEYS.LAST_FETCH}_home`, Date.now().toString());
        
        setError(null);
      } catch (apiError) {
        console.error('Failed to fetch home page:', apiError);
        // Use fallback if no cached data
        const currentTenant = currentTenantRef.current;
        const currentPage = currentPageRef.current;
        
        if (!currentPage && currentTenant) {
          const fallbackViewData = PageMapper.getDefaultHomeViewData(currentTenant);
          setViewData(fallbackViewData);
          setError('Usando configuración por defecto.');
        } else if (currentPage) {
          setError('Usando contenido en caché.');
        } else {
          setError('Error al cargar página de inicio.');
        }
      }

    } catch (error) {
      console.error('Error loading home page:', error);
      
      const currentTenant = currentTenantRef.current;
      if (currentTenant) {
        const fallbackViewData = PageMapper.getDefaultHomeViewData(currentTenant);
        setViewData(fallbackViewData);
        setError('Usando configuración por defecto debido a error.');
      } else {
        setError('Error al cargar página de inicio.');
      }
    } finally {
      setLoading(false);
      isLoading.current = false;
    }
  }, []);

  /**
   * Load home page data - routes to appropriate method based on context
   */
  const loadHomePageData = useCallback(async (forceRefresh = false): Promise<void> => {
    console.log('useHomePage: Loading data', { inIframe, forceRefresh });
    
    if (inIframe) {
      await loadHomePageDataViaPostMessage();
    } else {
      await loadHomePageDataViaAPI(forceRefresh);
    }
  }, [inIframe, loadHomePageDataViaPostMessage, loadHomePageDataViaAPI]);

  /**
   * Refetch home page data - stable function
   */
  const refetch = useCallback(async (): Promise<void> => {
    await loadHomePageData(true);
  }, [loadHomePageData]);

  // Setup postMessage listener for iframe mode
  useEffect(() => {
    if (inIframe) {
      console.log('useHomePage: Setting up postMessage listener for iframe mode');
      
      const handlePreviewUpdate = (data: any) => {
        console.log('useHomePage: Received preview update:', data);
        if (data.page && data.page.type === 'HOME') {
          setPage(data.page);
          setError(null);
        }
      };

      postMessageService.onPreviewUpdate(handlePreviewUpdate);
      
      return () => {
        postMessageService.cleanup();
      };
    }
  }, [inIframe]);

  // Load home page when tenant config is available
  useEffect(() => {
    if (!tenantLoading && tenantConfig && !isLoading.current) {
      const timeoutId = setTimeout(() => {
        loadHomePageData();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [tenantConfig, tenantLoading, loadHomePageData]);

  // Update view data when page or tenant config changes
  useEffect(() => {
    if (page && tenantConfig && !loading) {
      const mappedViewData = PageMapper.toViewData(page, tenantConfig);
      setViewData(mappedViewData);
    }
  }, [page, tenantConfig, loading]);

  return {
    page,
    viewData,
    loading: loading || tenantLoading,
    error,
    refetch,
  };
}

/**
 * Hook for multiple pages
 */
export function usePages(query: { type?: PageType; status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'; search?: string } = {}) {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState(null);
  
  const isLoading = useRef(false);
  const inIframe = isInIframe();

  /**
   * Load pages via postMessage (iframe mode)
   */
  const loadPagesViaPostMessage = useCallback(async (): Promise<void> => {
    if (isLoading.current) return;

    try {
      isLoading.current = true;
      setLoading(true);
      setError(null);
      
      const result = await postMessageService.getPages({
        type: query.type,
        status: query.status,
        search: query.search,
      });
      
      setPages(result.pages);
      setPagination(result.pagination);
    } catch (err) {
      console.error('Error loading pages via PostMessage:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar páginas via PostMessage');
    } finally {
      setLoading(false);
      isLoading.current = false;
    }
  }, [postMessageService, query.type, query.status, query.search]);

  /**
   * Load pages via API (non-iframe mode)
   */
  const loadPagesViaAPI = useCallback(async (): Promise<void> => {
    if (isLoading.current) return;

    try {
      isLoading.current = true;
      setLoading(true);
      setError(null);
      
      const result = await pageService.getPages({
        ...query,
        includeSections: false // Don't need sections for page lists
      });
      
      setPages(result.pages);
      setPagination(result.pagination);
    } catch (err) {
      console.error('Error loading pages via API:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar páginas');
    } finally {
      setLoading(false);
      isLoading.current = false;
    }
  }, [query.type, query.status, query.search]);

  /**
   * Load pages - routes to appropriate method based on context
   */
  const loadPages = useCallback(async (): Promise<void> => {
    if (inIframe) {
      await loadPagesViaPostMessage();
    } else {
      await loadPagesViaAPI();
    }
  }, [inIframe, loadPagesViaPostMessage, loadPagesViaAPI]);

  const refetch = useCallback(async (): Promise<void> => {
    await loadPages();
  }, [loadPages]);

  useEffect(() => {
    if (inIframe || pageService.hasPageAccess()) {
      loadPages();
    } else {
      setLoading(false);
      setError('Acceso denegado. Autenticación requerida.');
    }
  }, [loadPages, inIframe]);

  return {
    pages,
    loading,
    error,
    pagination,
    refetch,
  };
}